/**
 * All Encrypted Files Page (Public Metadata Listing)
 *
 * Security Design:
 * - Shows file METADATA ONLY for ALL users' files
 * - Does NOT expose: encrypted content, IV, file hash, storage path
 * - Owner sees "Decrypt" button (triggers OTP flow)
 * - Non-owner sees "Request Access" button
 * - Even after access is approved, decryption happens ONLY on the owner's device
 * - This preserves the zero-knowledge architecture
 *
 * Why this is safe to show to all users (for viva):
 * - File names and sizes alone cannot be used to decrypt
 * - Encryption keys never leave the client device
 * - No encrypted blob is downloadable from this page
 * - This is similar to how cloud storage shows shared file listings
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { listAllFiles, createAccessRequest, downloadApprovedFile, getApprovedFileMetadata, requestOTP, verifyOTP, logClientDecryptComplete } from '../services/api';
import { decryptFile } from '../services/encryption';
import { formatDateTime } from '../utils/time';

function AllFilesPage({ onDecryptRequest, currentUserId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestModal, setRequestModal] = useState(null); // file object
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => { loadFiles(); }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const data = await listAllFiles();
      setFiles(data || []);
    } catch (err) {
      console.error('[AllFiles] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Shared file decrypt state ──
  const [decryptModal, setDecryptModal] = useState(null); // file object
  const [decryptStep, setDecryptStep] = useState('otp');   // 'otp' then 'password'
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [decryptPassword, setDecryptPassword] = useState('');
  const [showDecryptPassword, setShowDecryptPassword] = useState(false);
  const [decryptStatus, setDecryptStatus] = useState('idle'); // idle, loading, decrypting, done, error
  const [decryptMessage, setDecryptMessage] = useState('');

  const handleDecryptSharedFile = async (file) => {
    setDecryptModal(file);
    setDecryptStep('otp');
    setOtpRequested(false);
    setOtpCode('');
    setOtpLoading(false);
    setOtpMessage('');
    setDecryptPassword('');
    setDecryptStatus('idle');
    setDecryptMessage('');
  };

  const handleRequestSharedOtp = async () => {
    if (!decryptModal) return;
    setOtpLoading(true);
    setOtpMessage('');
    try {
      const res = await requestOTP(decryptModal.id);
      setOtpRequested(true);
      setOtpMessage(res.message || 'Verification code sent. Check your email.');
    } catch (err) {
      setOtpMessage(err.message || 'Failed to send code.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifySharedOtp = async () => {
    if (!decryptModal || otpCode.length !== 6) return;
    setOtpLoading(true);
    setOtpMessage('');
    try {
      const res = await verifyOTP(decryptModal.id, otpCode);
      if (res.verified) {
        setDecryptStep('password');
        setOtpMessage('');
      } else {
        setOtpMessage('Verification failed. Please try again.');
      }
    } catch (err) {
      setOtpMessage(err.message || 'Invalid or expired code.');
    } finally {
      setOtpLoading(false);
    }
  };

  const executeDecryptShared = async () => {
    if (!decryptPassword) {
      setDecryptMessage('Please enter the encryption password.');
      return;
    }
    if (!decryptModal) return;

    setDecryptStatus('loading');
    setDecryptMessage('Fetching file metadata...');
    try {
      // Step 1: Get decryption metadata (salt, IV, iterations) from backend
      const meta = await getApprovedFileMetadata(decryptModal.id);
      if (!meta.salt) {
        setDecryptStatus('error');
        setDecryptMessage('Salt not available for this file. The file was uploaded before salt storage was enabled. Ask the owner to re-upload.');
        return;
      }

      // Step 2: Download the encrypted blob
      setDecryptMessage('Downloading encrypted file...');
      const encryptedData = await downloadApprovedFile(decryptModal.id);

      // Step 3: Decrypt client-side
      setDecryptStatus('decrypting');
      setDecryptMessage('Decrypting file locally in your browser...');
      const keyLength = meta.encryption_algorithm === 'AES-128-GCM' ? 128 : 256;
      const iterations = meta.pbkdf2_iterations || 100000;
      const decryptedData = await decryptFile(encryptedData, decryptPassword, meta.iv, meta.salt, keyLength, iterations);

      // Step 4: Auto-download decrypted file
      const blob = new Blob([decryptedData]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.original_filename || decryptModal.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDecryptStatus('done');
      setDecryptMessage('File decrypted and downloaded successfully!');
      setFeedback({ type: 'success', msg: 'Shared file decrypted and downloaded!' });
      try {
        await logClientDecryptComplete(decryptModal.id, 'shared');
      } catch {
        /* audit best-effort */
      }
      setTimeout(() => { setDecryptModal(null); setFeedback(null); }, 3000);
    } catch (err) {
      setDecryptStatus('error');
      const msg = err.message || 'Decryption failed';
      if (msg.includes('decrypt') || msg.includes('OperationError')) {
        setDecryptMessage('Wrong password or corrupted file. Please check the password with the file owner.');
      } else {
        setDecryptMessage(msg);
      }
    }
  };

  const handleRequestAccess = async () => {
    if (!requestModal) return;
    setSubmitting(true);
    try {
      await createAccessRequest(requestModal.id, reason);
      setFeedback({ type: 'success', msg: `Access request sent to ${requestModal.owner_email}` });
      setRequestModal(null);
      setReason('');
      loadFiles(); // refresh status
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to send request' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    const icons = {
      pdf: '📄', doc: '📝', docx: '📝', txt: '📄', xlsx: '📊', xls: '📊',
      csv: '📊', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', zip: '📦',
      rar: '📦', mp4: '🎬', mp3: '🎵', pptx: '📊', ppt: '📊',
    };
    return icons[ext] || '🔒';
  };

  const filtered = files.filter(f =>
    f.original_filename.toLowerCase().includes(search.toLowerCase()) ||
    f.owner_email.toLowerCase().includes(search.toLowerCase())
  );

  const ownerFiles = filtered.filter(f => f.is_owner);
  const otherFiles = filtered.filter(f => !f.is_owner);

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>All Encrypted Files</h2>
          <p style={s.subtitle}>
            Browse encrypted file metadata across all users. File content remains encrypted and inaccessible without owner approval.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search files or owners..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...s.searchInput, paddingRight: search ? 36 : 16 }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, lineHeight: 1 }}
                title="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          <button onClick={loadFiles} className="hover-icon-btn" style={s.refreshBtn} title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div style={{ ...s.toast, backgroundColor: feedback.type === 'success' ? 'var(--green)' : 'var(--red)' }}>
          {feedback.msg}
        </div>
      )}

      {/* Security Notice */}
      <div style={s.securityNotice}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span>
          All files are encrypted with <strong>AES-256-GCM</strong>. File content is inaccessible without the owner's encryption password and OTP verification.
        </span>
      </div>


      {/* Skeleton loading */}
      {loading && (
        <div style={s.fileGrid}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ ...s.fileCard, gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--border)', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite', animationDelay: `${i * 120}ms` }} />
                <div style={{ width: 60, height: 18, borderRadius: 8, background: 'var(--border)', opacity: 0.4 }} />
              </div>
              <div style={{ height: 14, width: '70%', borderRadius: 6, background: 'var(--border)', opacity: 0.4 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                <div style={{ height: 10, width: '100%', borderRadius: 4, background: 'var(--border)', opacity: 0.3 }} />
                <div style={{ height: 10, width: '80%', borderRadius: 4, background: 'var(--border)', opacity: 0.25 }} />
                <div style={{ height: 10, width: '60%', borderRadius: 4, background: 'var(--border)', opacity: 0.2 }} />
              </div>
              <div style={{ height: 42, borderRadius: 14, background: 'var(--border)', opacity: 0.25, marginTop: 'auto' }} />
            </div>
          ))}
        </div>
      )}

      {/* My Files Section */}
      {!loading && ownerFiles.length > 0 && (
        <>
          <h3 style={s.sectionTitle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            My Files ({ownerFiles.length})
          </h3>
          <div style={s.fileGrid}>
            {ownerFiles.map(file => (
              <FileCard
                key={file.id} file={file} formatSize={formatSize}
                getFileIcon={getFileIcon} onDecrypt={onDecryptRequest}
              />
            ))}
          </div>
        </>
      )}

      {/* Other Users' Files Section */}
      {!loading && otherFiles.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: 32 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Other Users' Files ({otherFiles.length})
          </h3>
          <div style={s.fileGrid}>
            {otherFiles.map(file => (
              <FileCard
                key={file.id} file={file} formatSize={formatSize}
                getFileIcon={getFileIcon} onRequestAccess={setRequestModal}
                onDecryptShared={handleDecryptSharedFile}
              />
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && files.length === 0 && (
        <div style={s.emptyState}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <p style={{ color: 'var(--text-secondary)', marginTop: 12, fontSize: 14 }}>No encrypted files in the system yet.</p>
        </div>
      )}

      {/* Search empty */}
      {!loading && files.length > 0 && filtered.length === 0 && (
        <div style={s.emptyState}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p style={{ color: 'var(--text-secondary)', marginTop: 12, fontSize: 14 }}>No files match "<strong>{search}</strong>"</p>
          <button onClick={() => setSearch('')} style={{ ...s.cancelBtn, marginTop: 8 }}>Clear search</button>
        </div>
      )}

      {/* Request Access Modal */}
      {requestModal && (
        <div style={s.modalOverlay} onClick={() => setRequestModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Request File Access</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              The file owner will review your request. Even if approved, only the owner can decrypt the file.
            </p>

            <div style={s.modalFileInfo}>
              <span style={{ fontSize: 22 }}>{getFileIcon(requestModal.original_filename)}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{requestModal.original_filename}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Owner: {requestModal.owner_email} · {formatSize(requestModal.file_size)}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-faint)', marginBottom: 6, display: 'block' }}>
                Reason for access (optional)
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Explain why you need access to this file..."
                style={s.textarea}
                rows={3}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setRequestModal(null)} style={s.cancelBtn}>Cancel</button>
              <button onClick={handleRequestAccess} disabled={submitting} className="btn-primary" style={s.submitBtn}>
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SHARED FILE DECRYPT MODAL ===== */}
      {decryptModal && ReactDOM.createPortal(
        <div style={s.modalOverlay} onClick={() => { if (decryptStatus !== 'loading' && decryptStatus !== 'decrypting' && !otpLoading) setDecryptModal(null); }}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Decrypt Shared File
            </h3>

            {/* File info */}
            <div style={s.modalFileInfo}>
              <span style={{ fontSize: 22 }}>{getFileIcon(decryptModal.original_filename)}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{decryptModal.original_filename}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Owner: {decryptModal.owner_email} &middot; {formatSize(decryptModal.file_size)}
                </div>
              </div>
            </div>

            {/* Step 1: OTP verification (required before download) */}
            {decryptStep === 'otp' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Step-up verification is required to download. Request a code to your email, then enter it below.
                </p>
                {otpMessage && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 12,
                    backgroundColor: otpMessage.includes('sent') || otpMessage.includes('Verification') ? 'var(--blue-muted)' : '#fef2f2',
                    color: otpMessage.includes('sent') || otpMessage.includes('Verification') ? 'var(--blue)' : 'var(--red)',
                    border: `1px solid ${otpMessage.includes('sent') || otpMessage.includes('Verification') ? '#bfdbfe' : '#fecaca'}`,
                  }}>
                    {otpMessage}
                  </div>
                )}
                {!otpRequested ? (
                  <button onClick={handleRequestSharedOtp} disabled={otpLoading} className="btn-primary" style={{ ...s.submitBtn, marginBottom: 16 }}>
                    {otpLoading ? 'Sending...' : 'Send verification code to my email'}
                  </button>
                ) : (
                  <>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-faint)', marginBottom: 6, display: 'block' }}>6-digit code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      style={{ ...s.textarea, resize: 'none', height: 'auto', padding: '10px 12px', textAlign: 'center', fontSize: 20, letterSpacing: 6, fontFamily: 'monospace', marginBottom: 12 }}
                      onKeyDown={e => { if (e.key === 'Enter') handleVerifySharedOtp(); }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                      <button type="button" onClick={() => { setOtpRequested(false); setOtpCode(''); setOtpMessage(''); }} className="saas-back-btn">
                        <span className="saas-back-btn__icon" aria-hidden>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                          </svg>
                        </span>
                        Back
                      </button>
                      <button onClick={handleVerifySharedOtp} disabled={otpLoading || otpCode.length !== 6} className="btn-primary" style={s.submitBtn}>
                        {otpLoading ? 'Verifying...' : 'Verify'}
                      </button>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => setDecryptModal(null)} style={s.cancelBtn} disabled={otpLoading}>Cancel</button>
                </div>
              </>
            )}

            {/* Step 2: Password and download */}
            {decryptStep === 'password' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  Enter the encryption password shared by the file owner. Decryption happens locally in your browser.
                </p>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, padding: '10px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 12, background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))', border: '1px solid rgba(245,158,11,0.25)' }}>
                  After decryption, the plaintext is saved on your device like a normal download. Server access expiry does not delete or control that local file — protect it yourself.
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', backgroundColor: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 'var(--radius-sm)', marginBottom: 16, fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>The server <strong>never sees your password</strong>. Decryption happens in your browser.</span>
                </div>
                {decryptMessage && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 12,
                    backgroundColor: decryptStatus === 'error' ? '#fef2f2' : decryptStatus === 'done' ? '#f0fdf4' : 'var(--blue-muted)',
                    color: decryptStatus === 'error' ? 'var(--red)' : decryptStatus === 'done' ? 'var(--green)' : 'var(--blue)',
                    border: `1px solid ${decryptStatus === 'error' ? '#fecaca' : decryptStatus === 'done' ? '#bbf7d0' : '#bfdbfe'}`,
                  }}>
                    {decryptMessage}
                  </div>
                )}
                {decryptStatus !== 'done' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-faint)', marginBottom: 6, display: 'block' }}>Encryption Password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showDecryptPassword ? 'text' : 'password'}
                        value={decryptPassword}
                        onChange={e => setDecryptPassword(e.target.value)}
                        placeholder="Password from file owner"
                        style={{ ...s.textarea, resize: 'none', height: 'auto', padding: '10px 40px 10px 12px', fontFamily: 'monospace' }}
                        disabled={decryptStatus === 'loading' || decryptStatus === 'decrypting'}
                        onKeyDown={e => { if (e.key === 'Enter') executeDecryptShared(); }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowDecryptPassword(!showDecryptPassword)}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}
                      >
                        {showDecryptPassword ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => { setDecryptStep('otp'); setDecryptPassword(''); setDecryptMessage(''); }}
                    className="saas-back-btn"
                    disabled={decryptStatus === 'loading' || decryptStatus === 'decrypting'}
                  >
                    <span className="saas-back-btn__icon" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                      </svg>
                    </span>
                    Back
                  </button>
                  <button onClick={() => setDecryptModal(null)} style={s.cancelBtn} disabled={decryptStatus === 'loading' || decryptStatus === 'decrypting'}>
                    {decryptStatus === 'done' ? 'Close' : 'Cancel'}
                  </button>
                  {decryptStatus !== 'done' && (
                    <button
                      onClick={executeDecryptShared}
                      disabled={decryptStatus === 'loading' || decryptStatus === 'decrypting' || !decryptPassword}
                      className="btn-primary"
                      style={{ ...s.submitBtn, backgroundColor: 'var(--blue)', opacity: (!decryptPassword || decryptStatus === 'loading' || decryptStatus === 'decrypting') ? 0.6 : 1 }}
                    >
                      {decryptStatus === 'loading' ? 'Fetching...' : decryptStatus === 'decrypting' ? 'Decrypting...' : 'Decrypt & Download'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── File Card Component ──────────────────────────────────────────
function FileCard({ file, formatSize, getFileIcon, onDecrypt, onRequestAccess, onDecryptShared }) {
  const statusColors = {
    PENDING: { bg: 'var(--yellow-muted)', color: 'var(--yellow)', label: 'Request Pending' },
    APPROVED: { bg: 'var(--green-muted)', color: 'var(--green)', label: 'Access Granted' },
    REJECTED: { bg: 'var(--red-muted)', color: 'var(--red)', label: 'Request Rejected' },
  };
  const statusInfo = statusColors[file.access_status];

  return (
    <div style={s.fileCard} className="hover-card">
      {/* File Header */}
      <div style={s.fileCardHeader}>
        <span style={{ fontSize: 28 }}>{getFileIcon(file.original_filename)}</span>
        {file.is_owner && <span className="security-badge blue" style={{ fontSize: 9 }}>OWNER</span>}
        {statusInfo && <span style={{ ...s.statusBadge, backgroundColor: statusInfo.bg, color: statusInfo.color }}>{statusInfo.label}</span>}
      </div>

      {/* File Name */}
      <div style={s.fileName}>{file.original_filename}</div>

      {/* Metadata */}
      <div style={s.fileMeta}>
        <div style={s.metaRow}>
          <span style={s.metaLabel}>Size</span>
          <span style={s.metaValue}>{formatSize(file.file_size)}</span>
        </div>
        <div style={s.metaRow}>
          <span style={s.metaLabel}>Encryption</span>
          <span className="security-badge green" style={{ fontSize: 9 }}>{file.encryption_algorithm}</span>
        </div>
        <div style={s.metaRow}>
          <span style={s.metaLabel}>Security</span>
          <span className="security-badge blue" style={{ fontSize: 9 }}>{file.security_level?.toUpperCase()}</span>
        </div>
        {!file.is_owner && (
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Owner</span>
            <span style={s.metaValue}>{file.owner_name || file.owner_email}</span>
          </div>
        )}
        <div style={s.metaRow}>
          <span style={s.metaLabel}>Uploaded</span>
          <span style={s.metaValue}>{formatDateTime(file.uploaded_at)}</span>
        </div>
      </div>

      {/* Action Button */}
      <div style={s.fileCardAction}>
        {file.is_owner ? (
          <button onClick={() => onDecrypt && onDecrypt(file.id)} className="btn-primary" style={s.decryptBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
            </svg>
            Decrypt
          </button>
        ) : file.access_status === 'PENDING' ? (
          <button disabled style={s.pendingBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Pending...
          </button>
        ) : file.access_status === 'APPROVED' ? (
          <button onClick={() => onDecryptShared && onDecryptShared(file)} className="hover-action-btn" style={s.approvedBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
            </svg>
            Decrypt &amp; Download
          </button>
        ) : (
          <button onClick={() => onRequestAccess && onRequestAccess(file)} className="hover-action-btn" style={s.requestBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            Request Access
          </button>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 18, flexWrap: 'wrap' },
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px', lineHeight: 1.1, letterSpacing: '-0.4px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, maxWidth: 520, fontWeight: 500, lineHeight: 1.6 },
  searchInput: {
    padding: '10px 16px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)',
    fontSize: 13, width: 240, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },
  refreshBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    cursor: 'pointer', color: 'var(--text-muted)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },

  securityNotice: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(6,182,212,0.04))',
    border: '1.5px solid rgba(99,102,241,0.12)', borderRadius: 18, marginBottom: 24, fontSize: 13,
    color: 'var(--text-faint)', backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 12px rgba(99,102,241,0.05)',
  },

  sectionTitle: {
    fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 14,
    display: 'flex', alignItems: 'center', gap: 10, letterSpacing: '-0.2px',
  },

  fileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 18 },

  fileCard: {
    background: 'var(--surface-85)', border: '1.5px solid rgba(99,102,241,0.08)', borderRadius: 22,
    padding: 24, display: 'flex', flexDirection: 'column', gap: 14,
    boxShadow: '0 3px 16px rgba(99,102,241,0.06), 0 1px 3px rgba(0,0,0,0.02)',
    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
    backdropFilter: 'blur(12px)',
  },
  fileCardHeader: { display: 'flex', alignItems: 'center', gap: 12 },
  fileName: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', wordBreak: 'break-word' },
  fileMeta: { display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
  metaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  metaLabel: { fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, letterSpacing: '0.2px' },
  metaValue: { fontSize: 12, color: 'var(--text-faint)', fontWeight: 500, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis' },
  statusBadge: { fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 12, letterSpacing: '0.2px' },

  fileCardAction: { marginTop: 'auto', paddingTop: 10 },
  decryptBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  requestBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 14, border: '1.5px solid rgba(16,185,129,0.2)',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.03))',
    color: 'var(--green)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  pendingBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 14, border: '1.5px solid rgba(245,158,11,0.2)',
    background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
    color: 'var(--yellow)', fontSize: 13, fontWeight: 600, cursor: 'default', opacity: 0.85,
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  approvedBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 14, border: '1.5px solid rgba(16,185,129,0.2)',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.04))',
    color: 'var(--green)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 3px 12px rgba(16,185,129,0.1)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },

  emptyState: { textAlign: 'center', padding: '56px 28px' },

  toast: {
    position: 'fixed', bottom: 28, right: 28, color: '#fff', padding: '12px 24px',
    borderRadius: 16, fontSize: 13, fontWeight: 700,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 999,
    backdropFilter: 'blur(10px)',
  },

  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    backdropFilter: 'blur(6px)',
  },
  modal: {
    backgroundColor: 'var(--surface-95)', borderRadius: 24, padding: 32,
    width: 460, maxWidth: '90vw',
    boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    border: '1.5px solid rgba(99,102,241,0.1)',
    backdropFilter: 'blur(20px)',
  },
  modalFileInfo: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))',
    borderRadius: 16, marginBottom: 18,
    border: '1px solid rgba(99,102,241,0.08)',
  },
  textarea: {
    width: '100%', padding: '12px 14px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', fontSize: 13,
    resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  cancelBtn: {
    padding: '10px 22px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    color: 'var(--text-faint)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  submitBtn: {
    padding: '10px 22px', borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(16,185,129,0.25)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
};

export default AllFilesPage;
